import {
  useEffect, useMemo, useRef, useState,
} from "react";
import { useInfiniteQuery, useQuery } from "react-query";
import { useSnackbar } from "@kitbird/core";
import { useWatch } from "react-hook-form";
import { IOptionItem } from "../../../../typings/common";
import { getAreas } from "../../../../services/areaService";
import {
  deleteBusiness, getBusinesses, getBusinessSegment, postBusiness, putBusiness,
} from "../../../../services/businessService/businessService";
import { MetadataI } from "../../../../services/businessService/types";
import useAuth from "../../../../components/Authentication/useAuth";
import { getMasterCompany, getMasterCompanyRelationByCompanyId } from "../../../../services/masterCompanyService";
import useBusinessForm, { BusinessFormValuesI, initialValues } from "./useBusinessForm";
import { getSegmentsByCompany } from "../../../../services/masterCompanySegmentService";
import { getPoolsAndHotels } from "../../../../services/poolHotelService";

export interface FilterI {
  search?: string,
  sortBy: string,
  sortType: "ASC" | "DSC" | "DEFAULT",
  pageNumber: number,
  pageSize: number,
}

const useBusiness = () => {
  const { userInfo, userAccount } = useAuth();
  const snackbar = useSnackbar();

  const [modalFormBusiness, setModalFormBusiness] = useState(false);
  const [idDeleteModal, setIdDeleteModal] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<BusinessFormValuesI>();
  const [isUpdateLoading, setUpdateLoading] = useState(false);
  const [segmentOpts, setSegmentOpts] = useState<IOptionItem[]>([]);
  const [areaOpts, setAreaOpts] = useState<IOptionItem[]>([]);
  const [poolOpts, setPoolOpts] = useState<IOptionItem[]>([]);
  const [companyOpts, setCompanyOpts] = useState<IOptionItem[]>([]);
  const [companyRelationOpts, setCompanyRelationOpts] = useState<IOptionItem[]>([]);
  const [params, setParams] = useState<FilterI>({
    pageNumber: 1,
    pageSize: 10,
    sortBy: "",
    sortType: "DSC",
  });
  const [meta, setMeta] = useState<MetadataI>({
    page: 0,
    page_size: 0,
    total_items: 0,
    total_pages: 0,
  });

  const {
    methods,

    handleCodeTypeChange,
    handleAreaChange,
    handlePoolChange,
    handleCompanyChange,
  } = useBusinessForm();

  const {
    control,
    getValues,
    setValue,
  } = methods;

  const codeTypeWatch = useWatch({ control, name: "codeType" });
  const areaWatch = useWatch({ control, name: "area" });
  const poolWatch = useWatch({ control, name: "pool" });
  const companyWatch = useWatch({ control, name: "company" });

  const { data: dataAreas } = useQuery({
    queryKey: ["get_areas"],
    queryFn: () => getAreas(),
  });

  useEffect(() => {
    if (dataAreas && dataAreas.listareaall && dataAreas.listareaall.length) {
      setAreaOpts(dataAreas.listareaall.map((area) => ({ label: area.name, value: area.id })));
    }
  }, [dataAreas]);

  const { data: dataPoolsAndHotels } = useQuery({
    queryKey: ["get_pools_hotels", codeTypeWatch],
    enabled: !!codeTypeWatch,
    queryFn: () => getPoolsAndHotels(codeTypeWatch),
  });

  useEffect(() => {
    if (dataPoolsAndHotels && areaWatch) {
      setPoolOpts(
        dataPoolsAndHotels.lstMtPoolModel
          .filter((item) => item.area_id === areaWatch?.value)
          .map((item) => ({ label: item.pool_name, value: item.pool_id }))
        || [],
      );
    } else setPoolOpts([]);
  }, [dataPoolsAndHotels, areaWatch]);

  const { data: dataCompanies } = useQuery({
    queryKey: ["get_companies", poolWatch],
    enabled: !!poolWatch && getValues("pool.value") !== "HO",
    queryFn: () => getBusinessSegment(
      userAccount.position_info[0].code_type,
      dataPoolsAndHotels?.lstMtPoolModel.find((item) => item.pool_id === (getValues("pool.value") || ""))?.area_id as string,
      getValues("pool.value") as string,
    ),
  });
  const { data: dataAllCompanies } = useQuery({
    queryKey: ["get_all_companies", poolWatch],
    enabled: !!poolWatch && getValues("pool.value") === "HO",
    queryFn: () => getMasterCompany(),
  });

  useEffect(() => {
    if (dataCompanies && dataCompanies.lstmstbusiness && dataCompanies.lstmstbusiness.length) {
      setCompanyOpts(dataCompanies.lstmstbusiness.map((company) => (
        { label: company.company_code, value: company.company_code }
      )));
    } else if (getValues("pool.value") === "HO" && dataAllCompanies?.list_mtcompany?.length) {
      setCompanyOpts(dataAllCompanies.list_mtcompany.map((company) => (
        { label: company.company_code, value: company.company_code }
      )));
    } else setCompanyOpts([]);
  }, [dataCompanies, dataAllCompanies, poolWatch]);

  const { data: dataCompanyRelations } = useQuery({
    queryKey: ["get_company_relations", companyWatch],
    enabled: !!companyWatch,
    queryFn: () => getMasterCompanyRelationByCompanyId(getValues("company.value") as string),
  });

  useEffect(() => {
    if (
      dataCompanyRelations
      && dataCompanyRelations.list_mtcompany
      && dataCompanyRelations.list_mtcompany.length
    ) {
      setValue("hasRelation", true);
      setCompanyRelationOpts(dataCompanyRelations.list_mtcompany.map((company) => (
        { label: company.company_friend, value: company.company_friend }
      )));
    } else {
      setValue("hasRelation", false);
      setValue("companyRelation", undefined);
      setCompanyRelationOpts([]);
    }
  }, [dataCompanyRelations]);

  const { data: dataSegments, isLoading: isSegmentsLoading } = useQuery({
    queryKey: ["get_segments", companyWatch],
    enabled: !!companyWatch,
    queryFn: () => getSegmentsByCompany(getValues("company.value") as string),
  });

  useEffect(() => {
    if (
      dataSegments
      && dataSegments.list_mtcompany_segment
      && dataSegments.list_mtcompany_segment.length
    ) {
      setSegmentOpts(dataSegments.list_mtcompany_segment.map((segment) => (
        { label: segment.desc_segment, value: segment.code_segment }
      )));
    } else setSegmentOpts([]);
  }, [dataSegments]);

  const fetchList = async (pageParam: number) => {
    const sortBy = params.sortType === "DEFAULT" ? "" : params.sortBy;
    const sortType = params.sortType !== "ASC" ? "DESC" : "ASC";

    return getBusinesses({
      search: params.search,
      sort_by: sortBy,
      sort_type: sortType,
      page_number: pageParam,
      page_size: params.pageSize,
    }).then((res) => {
      if (res.metadata) setMeta(res.metadata);
      return res.list_mst_business;
    }).catch(() => []);
  };

  const {
    data: businessesPages = { pages: [] },
    isFetching,
    isFetchingNextPage,
    refetch: refetchTable,
    fetchNextPage,
    remove,
    hasNextPage,
  } = useInfiniteQuery(
    ["businessList", params],
    async ({ pageParam = 1 }) => (
      fetchList(pageParam)
    ),
    {
      getNextPageParam: () => (
        meta.page < meta.total_pages
          ? meta.page + 1
          : undefined
      ),
      keepPreviousData: true,
    },
  );

  const businessList = useMemo(
    () => businessesPages.pages.flatMap((page) => page),
    [businessesPages, params],
  );

  const obsRef = useRef(null);
  const tableRef = useRef(document.createElement("table"));

  const options = {
    root: obsRef.current,
    rootMargin: "0px",
    threshold: [0],
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(({
      isIntersecting,
    }) => {
      if (isIntersecting && !isFetching) {
        fetchNextPage();
      } else if (meta.page === meta.total_pages && !isFetching) {
        fetchNextPage();
      }
    });
  }, options);

  useEffect(() => {
    if (meta.page === meta.total_pages) observer.unobserve(obsRef.current || document.createElement("tfoot"));
    else observer.observe(obsRef.current || document.createElement("tfoot"));
  }, [meta]);

  useEffect(() => {
    if (!modalFormBusiness) methods.reset(initialValues);
  }, [modalFormBusiness]);

  const onSubmit = methods.handleSubmit(
    async (form) => {
      setUpdateLoading(true);
      const basePayload = {
        area_id: form.area?.value as string || "",
        code_segment: form.segment?.value as string || "",
        code_type: form.codeType,
        company_code: form.company?.value as string || "",
        company_relation: form.companyRelation?.value as string || "",
        is_active: form.isActive,
        pool_id: form.pool?.value as string || "",
      };
      const action = !form.id ? "menambah" : "mengubah";
      const req = !form.id
        ? postBusiness({
          ...basePayload,
          create_by: userInfo.email,
        }) : putBusiness({
          ...basePayload,
          id: form.id,
          update_by: userInfo.email,
        });

      req.then((res) => {
        if (res.responseCode === "Succeed") {
          snackbar.show(`Berhasil ${action} data`, "success");
          setModalFormBusiness(false);
          refetchTable();
        } else {
          snackbar.show(res.responseMessage || `Gagal ${action} data`, "error");
        }
      }).catch(() => snackbar.show(`Gagal ${action} data`, "error"));

      setUpdateLoading(false);
    },
  );

  const onDelete = async () => {
    setUpdateLoading(true);
    if (idDeleteModal) {
      await deleteBusiness(idDeleteModal).then((res) => {
        if (res.responseCode === "Succeed") {
          snackbar.show("Berhasil menghapus data", "success");
          setIdDeleteModal(null);
          refetchTable();
        } else {
          snackbar.show(res.responseMessage || "Gagal menghapus data", "error");
        }
      }).catch(() => snackbar.show("Gagal menghapus data", "error"));
    }
    setUpdateLoading(false);
  };

  return {
    obsRef,
    tableRef,
    modalFormBusiness,
    setModalFormBusiness,
    idDeleteModal,
    setIdDeleteModal,
    editValue,
    setEditValue,
    isUpdateLoading,

    methods,
    handleCodeTypeChange,
    handleAreaChange,
    handlePoolChange,
    handleCompanyChange,

    segmentOpts,
    isSegmentsLoading,
    areaOpts,
    poolOpts,
    companyOpts,
    companyRelationOpts,
    params,
    setParams,
    meta,
    setMeta,

    businessList,
    isFetching,
    isFetchingNextPage,
    refetchTable,
    fetchNextPage,
    remove,
    hasNextPage,

    onSubmit,
    onDelete,
  };
};

export default useBusiness;
